import { biFunnelStages } from '@euler/model/bi/funnel';
import { BiQueryResult } from '@euler/model/bi/result';
import {
  BiVizType,
  isBiChartViz,
  kBiMaxRadarSeries,
  kBiMinFunnelStages,
  kBiMinRadarSpokes,
  kBiPyramidSides,
} from '@euler/model/bi/viz';
import { BiEditorState, biEditorProblem, biEditorViz } from './biEditorState';

function distinct(result: BiQueryResult, key: string): number {
  const index = result.columns.findIndex(column => column.key === key);
  if (index < 0) return 0;
  return new Set(result.rows.map(row => String(row[index]))).size;
}

/**
 * Whether the type picker offers a chart type. Every type is offered but
 * a radar, a pyramid or a funnel, which only show when the card's shape
 * and the preview's rows fit (a radar three spokes and five series at
 * most, a pyramid two sides, a funnel two stages with values), and a
 * KPI group whose shape fits it — and always while selected, so it can be
 * left.
 */
export function biEditorTypeOffered(
  state: BiEditorState,
  type: BiVizType,
  result: BiQueryResult | null,
): boolean {
  if (type === BiVizType.KpiGroup)
    return (
      state.vizType === type ||
      biEditorProblem({ ...state, vizType: type }) === null
    );
  if (
    type !== BiVizType.Radar &&
    type !== BiVizType.Pyramid &&
    type !== BiVizType.Funnel
  )
    return true;
  if (state.vizType === type) return true;
  const probe = { ...state, vizType: type };
  if (biEditorProblem(probe) || !result) return false;
  const viz = biEditorViz(probe);
  if (viz?.type === BiVizType.Funnel)
    return (
      biFunnelStages(viz, result).filter(stage => stage.value !== null)
        .length >= kBiMinFunnelStages
    );
  if (!viz || !isBiChartViz(viz)) return false;
  const splits = viz.seriesBy
    ? distinct(result, viz.seriesBy)
    : viz.series.length;
  if (type === BiVizType.Pyramid)
    return splits === kBiPyramidSides && distinct(result, viz.x) >= 1;
  return (
    splits <= kBiMaxRadarSeries && distinct(result, viz.x) >= kBiMinRadarSpokes
  );
}
